import { useCallback, useEffect, useRef, useState } from 'react';

import { useCreateSession } from '../../../queries';
import type { SessionDetailsResponse } from '../../../domain';
import { useRequireSignIn } from '../../../components/auth/useRequireSignIn';
import { SignInRequiredError } from '../../../components/auth/signInRequiredError';

import { buildWriterSessionCreateRequest } from './sessionHelpers';

export interface UseWriterLabSessionResult {
  sessionId: string | undefined;
  /** The active document draft (an Asset of kind "document"). */
  documentId: string | null;
  /** Capture the document id produced by an operation result. */
  captureDocument: (id: string | null | undefined) => void;
  ensureSession: () => Promise<string>;
  isCreatingSession: boolean;
  sessionError: Error | null;
}

/**
 * Studio-bound session lifecycle for Writer's Lab. Mirrors useImageLabSession:
 * lazily creates a session on the first `ensureSession()`; concurrent calls
 * coalesce onto one in-flight promise; switching studios resets the session.
 *
 * Unlike Image Lab, the "active asset" is a document draft — on the first
 * Generate the BE creates the session AND a document, and the result's
 * `documentId` is captured here via `captureDocument` so subsequent operations
 * (Refine/Expand/…) target the same draft.
 */
export function useWriterLabSession(studioId: string | undefined): UseWriterLabSessionResult {
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<Error | null>(null);
  const pendingRef = useRef<Promise<string> | null>(null);
  const createMutation = useCreateSession();
  const { requireSignIn } = useRequireSignIn();

  // Reset whenever the studio changes — each studio gets its own lifecycle.
  useEffect(() => {
    setSessionId(undefined);
    setDocumentId(null);
    setSessionError(null);
    pendingRef.current = null;
  }, [studioId]);

  const ensureSession = useCallback(async (): Promise<string> => {
    // Every text operation creates or reuses a session first, so the sign-in
    // gate sits here: a visitor is prompted before anything is sent.
    if (!requireSignIn()) throw new SignInRequiredError();
    if (sessionId) return sessionId;
    if (pendingRef.current) return pendingRef.current;
    const request = buildWriterSessionCreateRequest(studioId);
    const promise = createMutation
      .mutateAsync(request)
      .then((res: SessionDetailsResponse) => {
        setSessionId(res.sessionId);
        setSessionError(null);
        pendingRef.current = null;
        return res.sessionId;
      })
      .catch((err: unknown) => {
        const wrapped = err instanceof Error ? err : new Error(String(err));
        setSessionError(wrapped);
        pendingRef.current = null;
        throw wrapped;
      });
    pendingRef.current = promise;
    return promise;
  }, [sessionId, studioId, createMutation, requireSignIn]);

  const captureDocument = useCallback((id: string | null | undefined) => {
    if (id) setDocumentId(id);
  }, []);

  return {
    sessionId,
    documentId,
    captureDocument,
    ensureSession,
    isCreatingSession: createMutation.isPending,
    sessionError,
  };
}
